import React, { useEffect, useState } from "react";
import AdminLayout from "../components/AdminLayout";
import api, { formatDate, formatApiError } from "../lib/api";
import { Link } from "react-router-dom";
import { FileText, AlertTriangle, Send, Eye, Check } from "lucide-react";

const DEFAULT_BODY = `## Liability Waiver and Consent

Welcome to The Root Forum. By signing below, I acknowledge that:

1. The services provided are wellness services and are not a substitute for medical care.
2. I have shared any information relevant to scheduling my visit.
3. I release The Root Forum from liability for outcomes outside the scope of operational service.
4. I consent to digital records of my appointments, packages, and tincture recommendations.

We only collect what we need to schedule and care for you.`;

// Lightweight markdown -> HTML rendering (headings, bold, ordered/unordered lists, paragraphs)
function renderMarkdown(md) {
  if (!md) return "";
  const escape = (s) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const lines = md.split(/\r?\n/);
  const out = [];
  let listType = null;
  const closeList = () => { if (listType) { out.push(`</${listType}>`); listType = null; } };
  for (const raw of lines) {
    const line = raw.trim();
    if (!line) { closeList(); continue; }
    let m;
    if ((m = line.match(/^(#{1,6})\s+(.*)$/))) {
      closeList();
      const lvl = m[1].length;
      out.push(`<h${lvl} class="font-display text-${lvl === 1 ? "2xl" : lvl === 2 ? "xl" : "lg"} text-charcoal mt-4 mb-2">${escape(m[2])}</h${lvl}>`);
    } else if ((m = line.match(/^(\d+)\.\s+(.*)$/))) {
      if (listType !== "ol") { closeList(); out.push('<ol class="list-decimal pl-5 space-y-1 text-sm font-ui text-charcoal">'); listType = "ol"; }
      out.push(`<li>${escape(m[2]).replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")}</li>`);
    } else if ((m = line.match(/^[-*]\s+(.*)$/))) {
      if (listType !== "ul") { closeList(); out.push('<ul class="list-disc pl-5 space-y-1 text-sm font-ui text-charcoal">'); listType = "ul"; }
      out.push(`<li>${escape(m[1]).replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")}</li>`);
    } else {
      closeList();
      out.push(`<p class="text-sm font-ui text-charcoal leading-relaxed mb-2">${escape(line).replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")}</p>`);
    }
  }
  closeList();
  return out.join("\n");
}

export default function Waivers() {
  const [versions, setVersions] = useState([]);
  const [needsAttention, setNeedsAttention] = useState([]);
  const [draft, setDraft] = useState({ title: "Liability Waiver and Consent", body_markdown: DEFAULT_BODY, expiry_months: 12 });
  const [mode, setMode] = useState("list"); // list | edit
  const [selectedVersionId, setSelectedVersionId] = useState(null);
  const [previewMode, setPreviewMode] = useState(false);
  const [error, setError] = useState("");
  const [sending, setSending] = useState({});

  const load = async () => {
    const [{ data: vs }, { data: na }] = await Promise.all([
      api.get("/waivers"),
      api.get("/waivers/needs-attention"),
    ]);
    setVersions(vs);
    setNeedsAttention(na);
    if (!selectedVersionId && vs.length) setSelectedVersionId(vs[0].id);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const startNew = () => {
    const current = versions.find((v) => v.is_current);
    setDraft({
      title: current?.title || "Liability Waiver and Consent",
      body_markdown: current?.body_markdown || DEFAULT_BODY,
      expiry_months: current?.expiry_months || 12,
    });
    setMode("edit");
    setPreviewMode(false);
    setError("");
  };

  const publish = async () => {
    setError("");
    try {
      await api.post("/waivers", draft);
      setMode("list");
      setSelectedVersionId(null);
      await load();
    } catch (e) {
      setError(formatApiError(e));
    }
  };

  const sendReminder = async (clientId) => {
    setSending((s) => ({ ...s, [clientId]: "sending" }));
    try {
      await api.post(`/clients/${clientId}/waiver-reminder`);
      setSending((s) => ({ ...s, [clientId]: "sent" }));
    } catch {
      setSending((s) => ({ ...s, [clientId]: "error" }));
    }
  };

  const selectedVersion = versions.find((v) => v.id === selectedVersionId);
  const totalSignatures = versions.reduce((sum, v) => sum + (v.signed_count || 0), 0);

  return (
    <AdminLayout>
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.18em] text-sage mb-2">— Waivers</p>
          <h1 className="font-display text-4xl text-charcoal">Waiver versions</h1>
          <p className="text-sm font-ui text-slate mt-1">
            {versions.length} version{versions.length === 1 ? "" : "s"} on file · {totalSignatures} total signatures · {needsAttention.length} client{needsAttention.length === 1 ? "" : "s"} need attention
          </p>
        </div>
        {mode === "list" ? (
          <button onClick={startNew} className="px-4 py-2 bg-forest hover:bg-charcoal text-white text-sm font-ui rounded-md" data-testid="new-waiver-toggle">
            Publish new version
          </button>
        ) : (
          <div className="flex gap-2">
            <button onClick={() => setPreviewMode(!previewMode)} className="px-3 py-2 border border-hairline bg-white text-charcoal text-sm font-ui rounded-md hover:bg-cream flex items-center gap-1.5" data-testid="waiver-preview-toggle">
              <Eye className="w-4 h-4" /> {previewMode ? "Edit" : "Preview"}
            </button>
            <button onClick={() => { setMode("list"); setError(""); }} className="px-3 py-2 border border-hairline bg-white text-charcoal text-sm font-ui rounded-md hover:bg-cream" data-testid="waiver-cancel">Cancel</button>
            <button onClick={publish} className="px-4 py-2 bg-forest hover:bg-charcoal text-white text-sm font-ui rounded-md" data-testid="waiver-publish">Publish</button>
          </div>
        )}
      </div>

      {error && <div className="mb-4 px-4 py-3 bg-status-danger-bg/40 border border-status-danger/30 rounded-md text-sm font-ui text-status-danger" data-testid="waiver-error">{error}</div>}

      {mode === "edit" && (
        <div className="bg-white border border-hairline rounded-lg shadow-warm p-6 mb-6" data-testid="waiver-editor">
          <div className="grid grid-cols-2 gap-4 mb-4">
            <label className="block col-span-2">
              <span className="block text-xs font-ui text-slate mb-1">Title</span>
              <input className={inputCls} value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} data-testid="waiver-title" />
            </label>
            <label className="block col-span-2">
              <span className="block text-xs font-ui text-slate mb-1">Body (markdown)</span>
              {previewMode ? (
                <div
                  className="w-full min-h-[260px] px-4 py-3 bg-cream/40 border border-hairline rounded-md prose prose-sm max-w-none"
                  dangerouslySetInnerHTML={{ __html: renderMarkdown(draft.body_markdown) }}
                  data-testid="waiver-body-preview"
                />
              ) : (
                <textarea rows={12} className={inputCls + " font-mono text-xs"} value={draft.body_markdown} onChange={(e) => setDraft({ ...draft, body_markdown: e.target.value })} data-testid="waiver-body" />
              )}
            </label>
            <label className="block">
              <span className="block text-xs font-ui text-slate mb-1">Expiry (months)</span>
              <input type="number" min="1" className={inputCls} value={draft.expiry_months} onChange={(e) => setDraft({ ...draft, expiry_months: Number(e.target.value) })} data-testid="waiver-expiry" />
            </label>
          </div>
          <p className="text-xs font-ui text-slate">Publishing creates a new immutable version. Old signed waivers continue to reference the version they signed.</p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: versions list + preview */}
        <div className="lg:col-span-2 space-y-4">
          <div className="bg-white border border-hairline rounded-lg shadow-warm divide-y divide-hairline" data-testid="waiver-versions-list">
            <div className="px-6 py-3 grid grid-cols-12 text-[10px] font-mono uppercase tracking-[0.18em] text-slate">
              <span className="col-span-6">Title</span>
              <span className="col-span-3">Published</span>
              <span className="col-span-2 text-center">Signatures</span>
              <span className="col-span-1 text-right">ID</span>
            </div>
            {versions.length === 0 && <div className="px-6 py-12 text-center text-sm font-ui text-slate">No waiver versions published yet.</div>}
            {versions.map((v) => (
              <button
                key={v.id}
                onClick={() => setSelectedVersionId(v.id)}
                className={`w-full text-left px-6 py-4 grid grid-cols-12 items-center hover:bg-cream/30 transition ${selectedVersionId === v.id ? "bg-cream/40" : ""}`}
                data-testid={`waiver-version-row-${v.id}`}
              >
                <div className="col-span-6">
                  <p className="font-ui text-sm text-charcoal">
                    {v.title}
                    {v.is_current && <span className="ml-2 text-[10px] font-mono uppercase tracking-wider text-status-success bg-status-success-bg px-2 py-0.5 rounded-pill">current</span>}
                  </p>
                  <p className="text-xs font-ui text-slate mt-0.5">Expires after {v.expiry_months} months</p>
                </div>
                <div className="col-span-3 text-xs font-ui text-slate">{formatDate(v.published_at)}</div>
                <div className="col-span-2 text-center text-sm font-mono text-charcoal">{v.signed_count ?? 0}</div>
                <div className="col-span-1 text-right font-mono text-xs text-slate">{v.id.slice(0, 6)}</div>
              </button>
            ))}
          </div>

          {selectedVersion && (
            <div className="bg-white border border-hairline rounded-lg shadow-warm p-6" data-testid="waiver-version-preview">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4 text-sage" />
                  <h2 className="font-display text-xl text-charcoal">{selectedVersion.title}</h2>
                </div>
                <span className="font-mono text-xs text-slate">{selectedVersion.id.slice(0, 8)}</span>
              </div>
              <div
                className="prose prose-sm max-w-none border-t border-hairline pt-4"
                dangerouslySetInnerHTML={{ __html: renderMarkdown(selectedVersion.body_markdown) }}
              />
            </div>
          )}
        </div>

        {/* Right: attention queue */}
        <aside className="space-y-4">
          <div className="bg-white border border-hairline rounded-lg shadow-warm" data-testid="waivers-attention">
            <div className="px-5 py-4 border-b border-hairline flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-status-warning" />
              <h2 className="font-display text-lg text-charcoal">Needs attention</h2>
            </div>
            {needsAttention.length === 0 ? (
              <div className="px-5 py-8 text-center text-sm font-ui text-slate">All clients have current waivers on file.</div>
            ) : (
              <ul className="divide-y divide-hairline">
                {needsAttention.map((row) => {
                  const c = row.client;
                  const state = sending[c.id];
                  return (
                    <li key={c.id} className="px-5 py-3" data-testid={`waiver-attention-row-${c.id}`}>
                      <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <Link to={`/admin/clients/${c.id}`} className="font-ui text-sm text-charcoal hover:text-forest truncate block">
                            {c.first_name} {c.last_initial}.
                          </Link>
                          <p className="text-xs font-ui text-slate truncate">{c.email}</p>
                          <p className="text-[10px] font-mono uppercase tracking-wider mt-1">
                            {row.status === "missing"
                              ? <span className="text-status-danger">No waiver on file</span>
                              : <span className="text-status-warning">Expired {formatDate(row.expires_at)}</span>}
                          </p>
                        </div>
                        <button
                          disabled={state === "sending" || state === "sent"}
                          onClick={() => sendReminder(c.id)}
                          className="shrink-0 px-2.5 py-1.5 text-xs font-ui border border-forest text-forest rounded-md hover:bg-cream disabled:opacity-50 flex items-center gap-1"
                          data-testid={`send-waiver-reminder-${c.id}`}
                        >
                          {state === "sent" ? <><Check className="w-3 h-3" />Sent</> : state === "sending" ? "Sending…" : <><Send className="w-3 h-3" />Remind</>}
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </aside>
      </div>
    </AdminLayout>
  );
}

const inputCls = "w-full px-3 py-2 bg-white border border-hairline rounded-md text-sm font-ui text-charcoal focus:outline-none focus:border-sage focus:ring-2 focus:ring-sage/30";
